<template>
    <div class="lds-container">
        <div class="lm-order">
            <div class="lm-order__cart">
                <Cart />
            </div>
            <div class="lm-order__contacts">
                <form class="lm-order__form">
                    <order-recipient-fields
                        :initial-user="userData"
                        ref="recipientFields"
                        @update:user="onUserUpdate" />

                    <order-delivery-fields
                        v-if="isDeliveryAddressRequired"
                        ref="deliveryFields"
                        :initial-delivery="deliveryData"
                        @update:delivery="onDeliveryUpdate" />

                    <order-comment
                        @update:comment="onCommentUpdate" />

                    <div class="lm-order__form-sum">
                        <div class="lm-order__form-title">
                            Общая стоимость заказа
                        </div>
                        <div class="lm-order__form-discount-block"
                             v-if="cart.hasDiscount">
                            <div class="lm-order__form-total">
                                <div class="lm-order__form-total-text">
                                    Товары
                                </div>
                                <div class="lm-order__form-total-value -goods">
                                    {{ formatCoins(cartTotalAmountWithoutDiscount) }}
                                    <SvgIcon name="leads-coins" />
                                </div>
                            </div>
                            <div class="lm-order__form-total">
                                <div class="lm-order__form-total-text">
                                    Скидка
                                </div>
                                <div class="lm-order__form-total-value -discount">
                                    - {{ formatCoins(cartTotalDiscount) }}
                                    <SvgIcon name="leads-coins" />
                                </div>
                            </div>
                        </div>

                        <div class="lm-order__form-total">
                            <div class="lm-order__form-total-text">
                                Итого
                            </div>
                            <div class="lm-order__form-total-value">
                                {{ formatCoins(cartTotalAmount) }}
                                <SvgIcon name="leads-coins" />
                            </div>
                        </div>
                        <div class="lm-order__form-notice"
                             v-if="!canBuy">
                            Недостаточно средств
                        </div>
                    </div>
                    <button class="lds-btn lds-btn_fill lds-btn_size-lg lds-btn_full -dark"
                            :disabled="orderDisabled"
                            @click.prevent="makeOrder()">
                        Оформить заказ
                    </button>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import Cart from '@/components/market/MarketCart.vue';
import OrderRecipientFields from '@/components/market/order/OrderRecipientFields.vue';
import OrderDeliveryFields from '@/components/market/order/OrderDeliveryFields.vue';
import OrderComment from '@/components/market/order/OrderComment.vue';
import SvgIcon from '@/components/common/SvgIcon.vue';
import formattingCoins from '@/mixins/formattingCoins';
import { notDeliveryCategoriesIds } from '@/services/marketCategoriestConstants';

export default {
    name: 'OrderPage',
    components: {
        Cart,
        OrderDeliveryFields,
        OrderRecipientFields,
        OrderComment,
        SvgIcon,
    },
    mixins: [
        formattingCoins,
    ],
    data: () => ({
        userData: {
            fullName: '',
            phone: '',
            email: '',
            agreement: false,
        },
        deliveryData: {
            country: '',
            region: '',
            city: '',
            address: '',
        },
        comment: '',
    }),
    computed: {
        ...mapState('account', [
            'accountBalance',
        ]),
        ...mapState('market', [
            'loading',
            'cart',
            'products',
        ]),
        cartTotalAmount() {
            return this.cart.totalAmount;
        },
        cartTotalAmountWithoutDiscount() {
            return this.cart.totalOldAmount;
        },
        cartTotalDiscount() {
            return this.cart.totalDiscount;
        },
        canBuy() {
            if (this.accountBalance) {
                return parseFloat(this.accountBalance) >= parseFloat(this.cartTotalAmount);
            }
            return false;
        },
        orderDisabled() {
            return this.loading || this.cart.isEmpty || !this.canBuy;
        },
        userFieldsAreFilled() {
            const {
                agreement, fullName, phone, email,
            } = this.userData;
            return agreement && [fullName, phone, email].every(v => v.trim() !== '');
        },
        deliveryFieldsAreFilled() {
            const {
                country, region, address, city,
            } = this.deliveryData;
            return [country, region, address, city].every(v => v.trim() !== '');
        },
        isDeliveryAddressRequired() {
            const productIds = [];

            for (const position of Object.values(this.cart.cartPositions)) {
                productIds.push(position.product.id);
            }

            const val = productIds.some(productId => {
                const product = this.products[productId];

                if (!product) {
                    return false;
                }

                return !notDeliveryCategoriesIds.has(product.category.id);
            });
            return val;
        },
    },
    methods: {
        ...mapActions('account', [
            'loadBalance',
        ]),
        ...mapActions('market', [
            'sendOrder',
            'loadContacts',
        ]),
        ...mapMutations('common', [
            'setNotification',
        ]),
        ...mapMutations('market', [
            'setCartComment',
            'setCartDeliveryData',
            'setCartUserData',
        ]),
        onUserUpdate(user) {
            this.userData = { ...user };
        },
        onDeliveryUpdate(delivery) {
            this.deliveryData = { ...delivery };
        },
        onCommentUpdate(comment) {
            this.comment = comment;
        },
        makeOrder() {
            if (!this.userFieldsAreFilled || (this.isDeliveryAddressRequired && !this.deliveryFieldsAreFilled)) {
                const recRef = this.$refs.recipientFields;
                if (recRef && recRef.$v && recRef.$v.user) {
                    recRef.$v.user.$touch();
                }

                const delRef = this.$refs.deliveryFields;
                if (delRef && delRef.$v && delRef.$v.delivery) {
                    delRef.$v.delivery.$touch();
                }
                return;
            }
            if (!this.orderDisabled && !this.loading) {
                this.setCartComment(this.comment);
                this.setCartUserData(this.userData);
                this.setCartDeliveryData(this.isDeliveryAddressRequired ? this.deliveryData : null);
                return this.sendOrder()
                    .then(() => {
                        this.loadBalance();
                        this.loadContacts();
                        this.$router.push({
                            name: 'MarketOrders',
                        });
                        this.setNotification({
                            value: 'Ваш заказ успешно оформлен',
                            type: 'success',
                        });
                    })
                    .catch(error => {
                        this.setNotification({
                            value: error,
                            type: 'error',
                        });
                    });
            }
        },
    },
};
</script>
